import { Injectable } from "@angular/core";
import dayjs from "dayjs";
import { assertUnreachable } from "../../../../../../admin/shared/functions/assert-unreachable";
import { DateRange } from "../../../../../../shared/services/date.service";
import { PublicationTemplateType } from "../../../data/entities/publication-templates.entity";
import {
  PublicationTemplate,
  RecommendedSchedule,
} from "../../../domain/models/publication-template";
import "../../../../../../shared/extensions/date.extensions";
import { PublicationEventInput } from "../calendar-event-publication/publication-event-input";
import {
  FacebookCarouselCalendarEventFactory,
  FacebookPostCalendarEventFactory,
  FacebookStoryCalendarEventFactory,
} from "./facebook-calendar-event.mappers";
import { GooglePerformanceMaxCalendarEventFactory } from "./google-calendar-event.mappers";
import {
  InstagramCarouselCalendarEventFactory,
  InstagramSingleImageCalendarEventFactory,
  InstagramStoryCalendarEventFactory,
} from "./instagram-calendar-event.mappers";

export enum SocialMediaColor {
  Facebook = "#EFF6FF",
  Instagram = "#FBF1FC",
  LinkedIn = "#ECF8FF",
  Google = "#F0FDF4",
}

export enum SocialMediaBorderColor {
  Facebook = "#4A90E2",
  Instagram = "#A329A6",
  LinkedIn = "#0077B7",
  Google = "#16A34A",
}

export const mapPublicationTemplateToCalendarEvents = (
  publication: PublicationTemplate,
  schedule: RecommendedSchedule,
): PublicationEventInput => ({
  id: `${schedule.id}`,
  start: schedule.startDate.toISOString(),
  // fullCalendar does not display events on the end date, so we add one day
  end: dayjs(schedule.endDate?.toISOString())
    .add(1, "day")
    .toISOString(),
  allDay: true,
  durationEditable: publication.isAd,
  eventResizableFromStart: publication.isAd,
  eventDurationEditable: publication.isAd,
  extendedProps: {
    typeText: publication.type,
    publicationTemplateId: publication.id,
    platform: publication.platform,
    isAd: false,
  },
});

export interface IPublicationTemplateCalendarEventFactory {
  create(
    publicationTemplate: PublicationTemplate,
    campaignDateRange: DateRange,
  ): PublicationEventInput[];
}

@Injectable()
export class PublicationTemplateCalendarEventFactory
  implements IPublicationTemplateCalendarEventFactory
{
  public create(
    publicationTemplate: PublicationTemplate,
    campaignDateRange: DateRange,
  ): PublicationEventInput[] {
    let factory: IPublicationTemplateCalendarEventFactory;

    switch (publicationTemplate.type) {
      case PublicationTemplateType.InstagramCarousel:
        factory = new InstagramCarouselCalendarEventFactory();
        break;
      case PublicationTemplateType.InstagramStory:
        factory = new InstagramStoryCalendarEventFactory();
        break;
      case PublicationTemplateType.InstagramSingleImage:
        factory = new InstagramSingleImageCalendarEventFactory();
        break;
      case PublicationTemplateType.FacebookCarousel:
        factory = new FacebookCarouselCalendarEventFactory();
        break;
      case PublicationTemplateType.FacebookPost:
        factory = new FacebookPostCalendarEventFactory();
        break;
      case PublicationTemplateType.FacebookStory:
        factory = new FacebookStoryCalendarEventFactory();
        break;
      case PublicationTemplateType.GooglePerformanceMax:
        factory = new GooglePerformanceMaxCalendarEventFactory();
        break;
      default:
        return assertUnreachable(publicationTemplate as never);
    }

    return factory.create(publicationTemplate, campaignDateRange);
  }
}
